import React, { useRef, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Eraser, Check, X, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { brandName, brandEmail, brandPhone } from '../lib/brand';

/**
 * PickupReceipt
 * -------------
 * In-person pickup receipt: shows the order header, items table, total paid,
 * a signature canvas (finger / stylus / mouse), and a printed-name input.
 * On submit, POSTs `picked_up_by_name` + `signature_base64` to
 * `/api/proposals/{id}/pickup` which flips the proposal to status=picked_up
 * and stamps the timestamp + admin who released it.
 *
 * If the proposal already has a `pickup` record (signed earlier), we show
 * the read-only receipt with a Re-sign button to overwrite it (in case the
 * first signature was illegible).
 */
const PickupReceipt = ({ open, onClose, proposal, currentUser, onSaved }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPt = useRef(null);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [hasInk, setHasInk] = useState(false);
  const [saving, setSaving] = useState(false);
  // When `true`, show the existing pickup receipt (read-only). Re-sign
  // toggles back to the signing state.
  const [reSign, setReSign] = useState(false);

  const existing = proposal?.pickup;
  const isReadOnly = !!existing && !reSign;

  // ===== Canvas drawing ============================================
  const sizeCanvas = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    // Track device-pixel-ratio to keep ink crisp on retina screens.
    const ratio = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * ratio;
    c.height = rect.height * ratio;
    const ctx = c.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  useEffect(() => {
    if (!open || isReadOnly) return undefined;
    // Defer until the canvas is mounted in the DOM.
    const id = window.requestAnimationFrame(sizeCanvas);
    window.addEventListener('resize', sizeCanvas);
    return () => {
      window.cancelAnimationFrame(id);
      window.removeEventListener('resize', sizeCanvas);
    };
  }, [open, isReadOnly, sizeCanvas]);

  const getPt = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (e.clientX || e.touches?.[0]?.clientX || 0) - rect.left,
      y: (e.clientY || e.touches?.[0]?.clientY || 0) - rect.top,
    };
  };

  const onDown = (e) => {
    e.preventDefault();
    drawing.current = true;
    lastPt.current = getPt(e);
    setHasInk(true);
  };

  const onMove = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const pt = getPt(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPt.current = pt;
  };

  const onUp = () => { drawing.current = false; lastPt.current = null; };

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.restore();
    setHasInk(false);
  };

  // ===== Submit ====================================================
  const submit = async () => {
    if (!name.trim()) {
      toast.error('Please enter the pickup person\'s name');
      return;
    }
    if (!hasInk) {
      toast.error('Please sign in the box');
      return;
    }
    setSaving(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      const r = await axios.post(`/proposals/${proposal.id}/pickup`, {
        picked_up_by_name: name.trim(),
        signature_base64: dataUrl,
        notes: notes.trim(),
      });
      toast.success('Pickup recorded');
      if (onSaved) onSaved(r.data.pickup);
      setReSign(false);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save pickup');
    } finally {
      setSaving(false);
    }
  };

  // ===== Print =====================================================
  // Pop the receipt area into a fresh window for clean printing. We
  // serialize the DOM via outerHTML so styles inline correctly.
  const printReceipt = () => {
    const w = window.open('', '_blank', 'width=820,height=1080');
    if (!w) {
      toast.error('Pop-up blocked');
      return;
    }
    const node = document.getElementById('pickup-receipt-area');
    if (!node) return;
    const sigSrc =
      (existing && existing.signature_base64) ||
      (canvasRef.current && hasInk ? canvasRef.current.toDataURL('image/png') : '');
    w.document.write(`<!doctype html><html><head><title>Pickup Receipt #${proposal.proposal_number || ''}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 32px; color: #111; }
        h1 { font-size: 22px; margin: 0 0 4px; }
        .meta { color: #555; font-size: 13px; }
        table { width: 100%; border-collapse: collapse; margin: 16px 0; }
        th, td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #eee; font-size: 13px; }
        th { background: #f7f7f7; }
        .totals { margin-top: 16px; text-align: right; font-size: 14px; }
        .sig-box { margin-top: 24px; padding: 12px; border: 1px solid #ccc; border-radius: 6px; }
        .sig-box img { max-width: 320px; max-height: 120px; }
        .footer { margin-top: 32px; color: #666; font-size: 11px; text-align: center; }
      </style></head><body>${node.outerHTML.replace(/<canvas[^>]*>.*?<\/canvas>/g, sigSrc ? `<img src="${sigSrc}" alt="signature"/>` : '')}<script>window.onload=()=>{window.print();setTimeout(()=>window.close(),300);}</script></body></html>`);
    w.document.close();
  };

  if (!proposal) return null;

  const items = proposal.items || [];
  const releasedBy = isReadOnly ? existing.released_by : (currentUser?.email || currentUser?.name || 'admin');
  const ts = isReadOnly ? existing.picked_up_at : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="pickup-receipt-modal">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg">
              {isReadOnly ? 'Pickup Receipt (signed)' : 'Pickup Receipt — Sign for Order'}
            </DialogTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={printReceipt} data-testid="pickup-print">
                <Printer size={14} className="mr-1" /> Print
              </Button>
              <Button size="sm" variant="ghost" onClick={onClose} data-testid="pickup-close">
                <X size={16} />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div id="pickup-receipt-area">
          <h1>{brandName()} — Pickup Receipt</h1>
          <p className="meta">
            Order <strong>#{proposal.proposal_number || proposal.id?.slice(0, 8)}</strong> ·
            {' '}Customer: <strong>{proposal.customer_name}</strong>
            {proposal.customer_email && <> · {proposal.customer_email}</>}
          </p>

          <table>
            <thead>
              <tr><th>Item</th><th>Style</th><th>Color</th><th>Sizes</th><th style={{textAlign:'right'}}>Qty</th></tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx}>
                  <td>{it.brand || ''} {it.style || ''}</td>
                  <td>{it.style_number || it.styleID || '—'}</td>
                  <td>{it.color || '—'}</td>
                  <td>
                    {it.sizes
                      ? Object.entries(it.sizes).filter(([, q]) => Number(q) > 0)
                          .map(([s, q]) => `${s}:${q}`).join(', ')
                      : '—'}
                  </td>
                  <td style={{textAlign:'right'}}>{it.quantity || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="totals">
            <div>Subtotal: <strong>${Number(proposal.subtotal || 0).toFixed(2)}</strong></div>
            <div>Tax: <strong>${Number(proposal.tax_amount || 0).toFixed(2)}</strong></div>
            <div style={{fontSize:'16px',marginTop:'4px'}}>
              Total Paid: <strong style={{color:'#16a34a'}}>${Number(proposal.total_amount || 0).toFixed(2)}</strong>
            </div>
          </div>

          <div className="sig-box">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
              <strong>Signature</strong>
              {!isReadOnly && (
                <Button size="sm" variant="ghost" onClick={clearCanvas} data-testid="pickup-clear" type="button">
                  <Eraser size={14} className="mr-1" /> Clear
                </Button>
              )}
            </div>
            {isReadOnly ? (
              existing?.signature_base64 ? (
                <img
                  src={existing.signature_base64}
                  alt="signature"
                  style={{maxWidth: 320, maxHeight: 120, background:'#fff'}}
                  data-testid="pickup-signature-existing"
                />
              ) : (
                <p style={{color:'#777',fontSize:13}}>No signature recorded.</p>
              )
            ) : (
              <canvas
                ref={canvasRef}
                style={{
                  width: '100%',
                  height: 160,
                  background: '#fff',
                  border: '1px dashed #d4d4d8',
                  borderRadius: 4,
                  touchAction: 'none',
                  cursor: 'crosshair',
                }}
                onPointerDown={onDown}
                onPointerMove={onMove}
                onPointerUp={onUp}
                onPointerLeave={onUp}
                onPointerCancel={onUp}
                data-testid="pickup-signature-canvas"
              />
            )}
            <div className="meta" style={{marginTop:8,fontSize:12}}>
              {isReadOnly ? (
                <>
                  Picked up by <strong>{existing.picked_up_by_name}</strong>
                  {ts && (
                    <> on <strong>{new Date(ts).toLocaleString()}</strong></>
                  )} · Released by {releasedBy}
                  {existing.notes && (
                    <div style={{marginTop:6,color:'#555'}}>Notes: {existing.notes}</div>
                  )}
                </>
              ) : (
                <>By signing, you confirm you have received the order in good condition.</>
              )}
            </div>
          </div>
        </div>

        {!isReadOnly && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600 font-semibold">Printed name *</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Manny Fernandez"
                data-testid="pickup-name-input"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 font-semibold">Notes (optional)</label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., picked up 2 boxes of 24"
                data-testid="pickup-notes-input"
              />
            </div>
          </div>
        )}

        <div className="mt-5 flex justify-between items-center">
          <div className="text-xs text-gray-500">
            Released by <strong>{releasedBy}</strong>
            {!isReadOnly && <> · stamped at submit</>}
          </div>
          <div className="flex gap-2">
            {isReadOnly ? (
              <Button variant="outline" onClick={() => { setReSign(true); setName(''); setNotes(''); setHasInk(false); }} data-testid="pickup-resign">
                Re-sign
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={onClose} data-testid="pickup-cancel">Cancel</Button>
                <Button onClick={submit} disabled={saving} data-testid="pickup-submit">
                  <Check size={14} className="mr-1" />
                  {saving ? 'Saving…' : 'Confirm Pickup'}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PickupReceipt;
